import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Autocomplete,
  Typography,
} from '@mui/material';
import { useNotification } from '../../context/NotificationContext';

const statusOptions = ['Active', 'Expired', 'Pending', 'Rejected', 'Needs Review'];

function EditInsuranceDialog({ open, insurance, onClose, onSave, policyTypeOptions }) {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (insurance) {
      setFormData({
        companyName: insurance.companyName || '',
        policyNumber: insurance.policyNumber || '',
        policyType: insurance.policyType || '', // Changed from insuranceType
        coverageAmount: insurance.coverageAmount || '',
        status: insurance.status || 'Pending',
        effectiveDate: insurance.effectiveDate ? new Date(insurance.effectiveDate).toISOString().split('T')[0] : '',
        expirationDate: insurance.expirationDate ? new Date(insurance.expirationDate).toISOString().split('T')[0] : '',
        contactName: insurance.contactName || '',
        contactEmail: insurance.contactEmail || '',
        contactPhone: insurance.contactPhone || '',
        street1: insurance.address?.street1 || '',
        street2: insurance.address?.street2 || '',
        city: insurance.address?.city || '',
        state: insurance.address?.state || '',
        zip: insurance.address?.zip || '',
        country: insurance.address?.country || 'US',
      });
    } else {
      // Reset form if no insurance data is provided (e.g., when dialog closes and reopens for a new record, though this component is for edit)
      setFormData({
        companyName: '',
        policyNumber: '',
        policyType: '', // Changed from insuranceType
        coverageAmount: '',
        status: 'Pending',
        effectiveDate: '',
        expirationDate: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        street1: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
      });
    }
  }, [insurance, open]); // Rerun effect if insurance or open status changes

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAutocompleteChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = () => {
    // Basic client-side validation
    if (!formData.companyName || !formData.policyNumber) {
      const errorMsg = 'Company Name and Policy Number are required.';
      console.error(errorMsg);
      showNotification(errorMsg, 'error');
      return;
    }

    const updatedInsuranceData = {
      companyName: formData.companyName,
      policyNumber: formData.policyNumber,
      policyType: formData.policyType, // Changed from insuranceType
      coverageAmount: formData.coverageAmount ? parseFloat(formData.coverageAmount) : null,
      status: formData.status,
      effectiveDate: formData.effectiveDate || null,
      expirationDate: formData.expirationDate || null,
      contactName: formData.contactName,
      contactEmail: formData.contactEmail,
      contactPhone: formData.contactPhone,
      address: {
        street1: formData.street1,
        street2: formData.street2,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country,
      },
    };
    onSave(updatedInsuranceData, insurance._id);
  };

  if (!insurance) {
    return null; // Don't render if no insurance data
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Insurance Record</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company Name"
              name="companyName"
              value={formData.companyName || ''}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Policy Number"
              name="policyNumber"
              value={formData.policyNumber || ''}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={policyTypeOptions || []} // Use passed prop, ensure it's an array
              value={formData.policyType || null} // Changed from insuranceType
              onChange={(event, newValue) => handleAutocompleteChange('policyType', newValue)} // Changed from insuranceType
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')} // Handle if options are objects
              isOptionEqualToValue={(option, value) => typeof option === 'string' ? option === value : option.value === value} // Handle if options are objects
              renderInput={(params) => (
                <TextField {...params} label="Policy Type" variant="outlined" fullWidth /> // Changed label
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Coverage Amount"
              name="coverageAmount"
              type="number"
              value={formData.coverageAmount || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={statusOptions}
              value={formData.status || null}
              onChange={(event, newValue) => handleAutocompleteChange('status', newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Status" variant="outlined" fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Effective Date"
              name="effectiveDate"
              type="date"
              value={formData.effectiveDate || ''}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Expiration Date"
              name="expirationDate"
              type="date"
              value={formData.expirationDate || ''}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact Name"
              name="contactName"
              value={formData.contactName || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact Email"
              name="contactEmail"
              type="email"
              value={formData.contactEmail || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact Phone"
              name="contactPhone"
              value={formData.contactPhone || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={12} sx={{ mt: 1, mb: -1 }}>
             <Typography variant="subtitle2">Address</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Street 1"
              name="street1"
              value={formData.street1 || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Street 2"
              name="street2"
              value={formData.street2 || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="City"
              name="city"
              value={formData.city || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="State"
              name="state"
              value={formData.state || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Zip Code"
              name="zip"
              value={formData.zip || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Country"
              name="country"
              value={formData.country || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditInsuranceDialog;